"use client";

import {JSX} from "react";

import {useSnapshot} from "valtio";

import {type InquiryFormInstance} from "@/features/rental/inquiry-form/inquiry-form.model";
import {BookingInfoBlock} from "@/features/rental/inquiry-form/parts/booking-info-block";
import {InquiryFormActions} from "@/features/rental/inquiry-form/parts/inquiry-form-actions";
import {InquiryFormFields} from "@/features/rental/inquiry-form/parts/inquiry-form-fields";
import {InquiryModalHeader} from "@/features/rental/inquiry-form/parts/inquiry-modal-header";
import {InquiryPricingBlock} from "@/features/rental/inquiry-form/parts/inquiry-pricing-block";

import {useMatchMedia} from "@/shared/hooks";
import {useTranslation} from "@/shared/i18n";
import {CloseButton} from "@/shared/ui/buttons";
import {FormSubmitFeedbackSuccess} from "@/shared/ui/form/form-submit-feedback";
import {Drawer} from "@/shared/ui/overlay/drawer";
import {Modal} from "@/shared/ui/overlay/modal";

import "./styles/inquiry-form.scss";

export interface InquiryFormModalProps {
    model: InquiryFormInstance;
    isOpen: boolean;
    onClose: () => void;
}

export function InquiryFormModal({model, isOpen, onClose}: InquiryFormModalProps): JSX.Element {
    const isMobile = useMatchMedia({maxWidth: 767});
    const snapshot = useSnapshot(model);
    const formId = "inquiry-form";
    const {t} = useTranslation("booking");

    if (isMobile) {
        return (
            <Drawer open={isOpen} onClose={onClose} direction="bottom" zIndex={1200}>
                <div className="inquiry-form-drawer">
                    <div className="inquiry-form-drawer__header">
                        <InquiryModalHeader unitName={model.unitName} />
                        <CloseButton onClick={onClose} />
                    </div>
                    <div className={`inquiry-form-drawer__body${snapshot.submitFeedback.isSuccess ? " inquiry-form-drawer__body--success" : ""}`}>
                        {snapshot.submitFeedback.isSuccess ? (
                            <FormSubmitFeedbackSuccess model={model.submitFeedback} />
                        ) : (
                            <>
                                {snapshot.bookingInfo && (
                                    <div className="inquiry-form-drawer__property">
                                        <BookingInfoBlock info={snapshot.bookingInfo} unitName={model.unitName} />
                                    </div>
                                )}
                                <InquiryFormFields model={model} formId={formId} />
                                {snapshot.pricing && (
                                    <div className="inquiry-form__pricing">
                                        <p className="inquiry-pricing-disclaimer">{t("inquiryOnlyMessage")}</p>
                                        <InquiryPricingBlock pricing={snapshot.pricing} showBookingInfo={false} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {!snapshot.submitFeedback.isSuccess && (
                        <div className="inquiry-form-drawer__footer">
                            <InquiryFormActions model={model} formId={formId} />
                        </div>
                    )}
                </div>
            </Drawer>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            lockScroll
            usePortal
            bodyPadding={0}
            borderRadius={16}
            contentClassName="inquiry-form-modal-content"
            header={
                <div className="inquiry-form-modal-header-wrapper">
                    <InquiryModalHeader unitName={model.unitName} />
                    <p className="inquiry-pricing-disclaimer">{t("inquiryOnlyMessage")}</p>
                </div>
            }
            ariaLabel="Inquiry form"
        >
            <div className={`inquiry-form-modal-body${snapshot.submitFeedback.isSuccess ? " inquiry-form-modal-body--success" : ""}`}>
                {snapshot.submitFeedback.isSuccess ? (
                    <FormSubmitFeedbackSuccess model={model.submitFeedback} />
                ) : (
                    <>
                        <div className="inquiry-form-modal-body__form">
                            <InquiryFormFields model={model} formId={formId} />
                            <div className="inquiry-form-actions">
                                <InquiryFormActions model={model} formId={formId} />
                            </div>
                        </div>
                        {snapshot.pricing && (
                            <div className="inquiry-form-modal-body__pricing">
                                <InquiryPricingBlock pricing={snapshot.pricing} bookingInfo={snapshot.bookingInfo} unitName={model.unitName} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
